import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import debounce from 'react-utils/debounce';
import styled from 'styled-components';
import { GREAT_WHITE } from 'HubStyleTokens/colors';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import { getFormData, getHar, getIsMultipart, getOas, getOperation, getAvailableClientLibraries // @ts-expect-error No types given for file
} from '../../../modules/Doc/selectors/DocSelectors'; //from 'developer-docs-shared-components/modules/Doc/selectors/DocSelectors';
// @ts-expect-error No types given for file

import Markdown from '../../../modules/Doc/components/Markdown';
import ClickableHeader from '../../../modules/Header/ClickableHeader'; // @ts-expect-error No types given for file

import ErrorBoundary from '../../../modules/ErrorBoundary/ErrorBoundary'; // @ts-expect-error No types given for file

import { updateFormData } from '../../../modules/Doc/actions'; // @ts-expect-error No types given for file

import removeUndefinedObjects from '../../../lib/api-explorer/remove-undefined-objects';
import { OasPropType, OperationPropType // @ts-expect-error No types given for file
} from '../../../lib/api-explorer/models/Oas'; // @ts-expect-error No types given for file

import { getRequestBodyExample } from '../../../modules/Doc/utils/ExampleUtils';
import NewParams from './NewParams';
import H4 from 'UIComponents/elements/headings/H4';
import NewRequirements from '../../../modules/Doc/components/NewRequirements';
import Response from '../../Response/components/Response'; // @ts-expect-error No types given for file

import PathUrl from '../../../modules/Doc/components/PathUrl';
import { stringToId } from '../../../utils/urls'; // @ts-expect-error No types given for file

import CodeSample from '../../../modules/CodeSample/components/CodeSample';
import CardComp from '../../../components/endpoint/CardComp';
import LeftColumnComp from '../../../components/endpoint/LeftColumnComp';
import RightColumnComp from '../../../components/endpoint/RightColumnComp';
const PathContainer = styled(UIGridItem).withConfig({
  displayName: "NewDoc__PathContainer",
  componentId: "j88as5-0"
})(["&&{padding:0;}"]);
const StyledRequirementsPanel = styled.div.withConfig({
  displayName: "NewDoc__StyledRequirementsPanel",
  componentId: "j88as5-1"
})(["border:1px solid ", ";border-radius:10px;margin-bottom:16px;padding:16px;"], GREAT_WHITE);

const Header = ({
  doc,
  oas,
  oasDocs,
  operation,
  isMultipart,
  isDirty
}) => {
  const renderPathUrl = () => {
    return /*#__PURE__*/_jsx(PathUrl, {
      doc: doc,
      dirty: isDirty,
      oas: oas,
      oasDocs: oasDocs,
      operation: operation,
      isMultipart: isMultipart,
      newLayout: true
    });
  };

  return /*#__PURE__*/_jsx("div", {
    className: "p-right-4",
    children: (doc.title || doc.excerpt) && /*#__PURE__*/_jsxs("div", {
      className: "m-bottom-6",
      children: [doc.title && /*#__PURE__*/_jsx(ClickableHeader, {
        Component: H4,
        id: stringToId(`${doc.api.method}-${doc.swagger.path}`),
        className: "m-bottom-8",
        children: doc.title
      }), /*#__PURE__*/_jsx(PathContainer, {
        className: "m-bottom-6",
        size: 12,
        children: doc.type === 'endpoint' && renderPathUrl()
      }), doc.excerpt && /*#__PURE__*/_jsx("div", {
        className: "excerpt m-bottom-8",
        children: /*#__PURE__*/_jsx(Markdown, {
          children: doc.excerpt
        })
      })]
    })
  });
};

function Doc(props) {
  const populateFormDataWithExample = () => {
    const {
      operation,
      oas,
      formData
    } = props;

    if (operation.requestBody && !removeUndefinedObjects(formData.body)) {
      const exampleRequestBody = getRequestBodyExample(operation, oas, false);

      if (exampleRequestBody) {
        try {
          formData.body = JSON.parse(exampleRequestBody.code);
        } catch (__) {
          // some examples aren't JSON parseable (i.e. "string") so just display the code
          formData.body = exampleRequestBody.code;
        }
      }
    }

    return formData;
  };

  const [state, setState] = useState({
    dirty: false,
    formData: populateFormDataWithExample(),
    initialFormData: populateFormDataWithExample(),
    hasError: false
  });
  const debouncedUpdateFormData = debounce(props.updateFormData, 200);
  useEffect(() => {
    const hash = props.location.hash.substring(1);

    if (hash && hash.length) {
      window.requestAnimationFrame(() => {
        const el = document.getElementById(`anchor-${decodeURIComponent(hash)}`);

        if (el) {
          el.scrollIntoView();
        }
      });
    }
  }, [props.location.hash]);

  const resetFormState = () => {
    setState({
      hasError: false
    });
  };

  const onFormDataChange = formData => {
    setState(prevState => {
      return {
        formData: Object.assign({}, prevState.formData, {}, formData),
        dirty: true
      };
    });
    debouncedUpdateFormData(formData, props.doc._id);
  };

  const registerError = () => {
    const {
      initialFormData
    } = state;
    /*
      This is placed here in order to prevent a race condition from occurring.
      When placed in `resetFormState`, the form will only reset after hitting
      the button to acknowledge the error occurred in the boundary component
      twice. Moving it here removed this condition.
    */

    onFormDataChange(initialFormData);
    setState({
      hasError: true
    });
  };

  const renderCodeSample = () => {
    const {
      hasError
    } = state;
    const {
      doc,
      har,
      isMultipart,
      language,
      oas,
      oasDocs,
      operation,
      setLanguage,
      availableClientLibraries
    } = props;
    let examples;

    try {
      examples = doc.api.examples.codes;
    } catch (e) {
      examples = [];
    }

    return /*#__PURE__*/_jsx(ErrorBoundary, {
      hasError: hasError,
      resetFormState: resetFormState,
      registerError: registerError,
      children: /*#__PURE__*/_jsx(CodeSample, {
        doc: doc,
        har: har,
        isMultipart: isMultipart,
        oas: oas,
        oasDocs: oasDocs,
        setLanguage: setLanguage,
        operation: operation,
        formData: state.formData,
        language: language,
        examples: examples,
        availableClientLibraries: availableClientLibraries,
        newLayout: true
      })
    });
  };

  const renderResponse = () => {
    const {
      doc,
      oas,
      operation
    } = props;
    let exampleResponses;

    try {
      exampleResponses = props.doc.api.results.codes;
    } catch (e) {
      exampleResponses = [];
    }

    return /*#__PURE__*/_jsx(Response, {
      docId: doc._id,
      newLayout: true,
      oas: oas,
      operation: operation,
      exampleResponses: exampleResponses
    });
  };

  const renderParams = () => {
    const {
      oas,
      operation
    } = props;
    return /*#__PURE__*/_jsx(NewParams, {
      formData: state.formData,
      oas: oas,
      onChange: onFormDataChange,
      operation: operation
    });
  };

  const {
    hasError
  } = state;
  const {
    doc,
    operation
  } = props;
  const columnSpan = useMemo(() => ({
    base: 12,
    laptop: 6
  }), []);
  return /*#__PURE__*/_jsxs(CardComp, {
    testId: `doc-${doc.api.method}-${doc.swagger.path}`,
    children: [/*#__PURE__*/_jsxs(LeftColumnComp, {
      columnSpan: columnSpan,
      children: [/*#__PURE__*/_jsx(Header, {
        doc: doc,
        isDirty: state.dirty,
        isMultipart: props.isMultipart,
        oas: props.oas,
        oasDocs: props.oasDocs,
        operation: props.operation
      }), /*#__PURE__*/_jsx(StyledRequirementsPanel, {
        children: /*#__PURE__*/_jsx(NewRequirements, {
          newLayout: true,
          doc: doc,
          operation: operation
        })
      }), doc.type === 'endpoint' && !hasError && renderParams(), doc.body && /*#__PURE__*/_jsx("span", {
        children: "DocBody"
      })]
    }), /*#__PURE__*/_jsxs(RightColumnComp, {
      columnSpan: columnSpan,
      children: [doc.type === 'endpoint' && /*#__PURE__*/_jsxs(Fragment, {
        children: [renderCodeSample(), renderResponse()]
      }), doc.body && /*#__PURE__*/_jsx("span", {
        children: "DocBody"
      })]
    })]
  });
}

Doc.propTypes = {
  doc: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    excerpt: PropTypes.string,
    slug: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    body: PropTypes.string,
    api: PropTypes.shape({
      method: PropTypes.string.isRequired,
      params: PropTypes.object,
      examples: PropTypes.shape({
        codes: PropTypes.arrayOf(PropTypes.shape({
          language: PropTypes.string.isRequired,
          code: PropTypes.string.isRequired
        }))
      }),
      results: PropTypes.shape({
        codes: PropTypes.arrayOf(PropTypes.shape({}))
      })
    }),
    swagger: PropTypes.shape({
      path: PropTypes.string.isRequired
    })
  }).isRequired,
  flags: PropTypes.shape({
    correctnewlines: PropTypes.bool
  }).isRequired,
  formData: PropTypes.object.isRequired,
  har: PropTypes.shape({}).isRequired,
  isMultipart: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  location: PropTypes.object.isRequired,
  oas: OasPropType.isRequired,
  oasDocs: PropTypes.array.isRequired,
  operation: OperationPropType.isRequired,
  setLanguage: PropTypes.func.isRequired,
  updateFormData: PropTypes.func.isRequired,
  availableClientLibraries: PropTypes.array.isRequired
};
Doc.defaultProps = {
  oas: {},
  flags: {
    correctnewlines: false
  }
};

const mapStateToProps = (state, ownProps) => {
  return {
    formData: getFormData(state, ownProps),
    har: getHar(state, ownProps),
    isMultipart: getIsMultipart(state, ownProps),
    oas: getOas(state),
    operation: getOperation(state, ownProps),
    availableClientLibraries: getAvailableClientLibraries(state)
  };
};

const mapDispatchToProps = {
  updateFormData
};
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Doc));